import { ChakraProvider } from '@chakra-ui/react';
import { FC, PropsWithChildren, useMemo } from 'react';

import { useCurrentCompany } from '@bonusly/who-am-i';

import { theme as baseTheme, createTheme } from '../theme';
import { resolveAccentColorFromHex, toSemanticTokenOverrides } from '../theme/company-accent-colors';

/**
 * CompanyThemeProvider
 *
 * Wraps content in a ChakraProvider with the company's accent color applied
 * via Chakra semantic token overrides. No !important, no <style> injection.
 *
 * Reads theme_color from the company identity profile. When the color changes
 * (e.g., admin saves a new theme), the provider re-creates the theme and
 * re-renders children with the updated tokens.
 *
 * Place inside AuthProvider (needs useCurrentCompany) and inside the base
 * ThemeProvider (extends the base theme).
 */
export const CompanyThemeProvider: FC<PropsWithChildren> = ({ children }) => {
	const company = useCurrentCompany();
	const themeColor = company?.custom_theme?.theme_color ?? null;

	const companyTheme = useMemo(() => {
		const accentColor = resolveAccentColorFromHex(themeColor);
		if (!accentColor) return baseTheme;

		const overrides = toSemanticTokenOverrides(accentColor);
		if (!overrides) return baseTheme;

		return createTheme({ semanticTokens: overrides });
	}, [themeColor]);

	return (
		<ChakraProvider theme={companyTheme} resetCSS={false}>
			{children}
		</ChakraProvider>
	);
};

CompanyThemeProvider.displayName = 'CompanyThemeProvider';
